import { useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { GetOrganizationDomains } from 'Apollo/queries/organization/GetOrganizationDomains';
import EmptyState from 'Components/Shared/EmptyState';
import ErrorMsg from 'Components/Shared/ErrorMsg/ErrorMsg';
import useNotification from 'Hooks/useNotification';
import useTableHandlers from 'Hooks/useTableHandlers';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ROW_PER_PAGE_DEFAULT } from 'Theme/config';
import { calculateNewPage } from 'Tools/calculateNewPage';
import OrganizationDomains from './OrganizationDomains';

interface Props {
  organization: IOrganization;
}

function OrganizationDomainsContainer({ organization }: Readonly<Props>) {
  const { onError } = useNotification();
  const { organizationCode } = useParams<{ organizationCode: string }>();
  const [selectedIds, setSelectedIds] = useState<string[] | null>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROW_PER_PAGE_DEFAULT);
  const [currentPage, setCurrentPage] = useState<number>(0);

  let { data, loading, error } = useQuery<
    GetOrganizationDomainsData,
    GetOrganizationDomainsVars
  >(GetOrganizationDomains, {
    fetchPolicy: 'cache-and-network',
    variables: {
      code: organizationCode,
      page: {
        first: rowsPerPage,
        offset: currentPage * rowsPerPage
      }
    },
    onError
  });

  let rows: Array<IOrganizationDomain> = [];
  let totalCount = 0;

  const onChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const onUpdate = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const { handleSelect, handleUnselect } = useTableHandlers({
    onUpdate,
    selectedIds
  });

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(calculateNewPage(currentPage, rowsPerPage, newRowsPerPage));
  };

  if (data?.organizationDomains) {
    rows = data.organizationDomains.edges.map(d => d.node);
    totalCount = data.organizationDomains.totalCount;
  }

  if (loading) {
    return (
      <EmptyState icon={<CircularProgress />} primary={' '} secondary={' '} />
    );
  }

  if (!loading && error) return <ErrorMsg />;

  return (
    <OrganizationDomains
      loading={loading}
      organization={organization}
      domains={rows}
      totalCount={totalCount}
      currentPage={currentPage}
      onChangePage={onChangePage}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onSelect={handleSelect}
      onUnselect={handleUnselect}
      selectedIds={selectedIds}
    />
  );
}
export default OrganizationDomainsContainer;
